import Header from 'components/headers/bulletins'
import PaymentIcon from 'assets/images/buySubscription/payment-selected.svg'
import useSetCommission from './hooks/useSetCommission'
import { useParams } from 'react-router-dom'
import { toggleSupportModal } from 'store/redux/sidebar'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store/redux'
import SupportModal from 'components/modals/supportModal'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'

/** Days of a paid subscription we're willing to write off silently.
 *  Switching to the commission plan does NOT refund the rest of the period —
 *  the days stay on the order, unused, and members start paying 2% per cash-in
 *  on top of a period the fund has already paid for. Under this many days the
 *  loss is small enough to just warn about; above it, we stop and tell them. */
const IGNORABLE_REMAINING_DAYS = 5

export default function Commission() {
    const { moneyPool } = useParams()
    const { mutate } = useSetCommission()
    const dispatch = useDispatch()
    const { isOpenSupportModal } = useSelector(
        (state: RootState) => state.sideBarControler
    )
    const { moneyPoolManagement, isLoadingMoneypoolManagement } =
        useGetMoneyPoolManagement(moneyPool)

    const remainingDays = moneyPoolManagement?.service_package_remaining_days ?? 0
    const hasPaidDaysLeft = remainingDays > 0
    const isBlocked = remainingDays > IGNORABLE_REMAINING_DAYS
    const persianDays = remainingDays.toLocaleString('fa-IR')
    return (
        <>
            <div className=" flex min-h-screen flex-col bg-canvas">
                <div className=" grow-0">
                    <Header title={`ارتقاء صندوق`} />
                </div>
                <div className=" grow px-2">
                    <p className=" my-3 text-center">
                        نسخه‌ی انتخابی شما برای ارتقای صندوق پس‌انداز
                    </p>
                    {isBlocked && (
                        <div className=" rounded-card border border-danger bg-danger-soft p-3 mb-3">
                            <p className=" text-sm font-bold text-danger-on">
                                این صندوق اشتراک فعال دارد
                            </p>
                            <p className=" text-sm mt-2">
                                {/* No em-dash: a lone «—» inside an RTL line
                                    drifts away from the clause it belongs to. */}
                                {persianDays} روز از اشتراک این صندوق باقی
                                مانده است. با تغییر به نسخه‌ی کارمزدی، این
                                روزها بازگردانده نمی‌شوند و اعضا از همین حالا
                                بابت هر واریز کارمزد می‌پردازند؛ یعنی بابت
                                دوره‌ای که هزینه‌اش را قبلاً پرداخت کرده‌اید.
                            </p>
                            <p className=" text-sm mt-2">
                                لطفاً پس از پایان اشتراک اقدام کنید، یا اگر
                                می‌خواهید همین حالا تغییر دهید با مشاور همیان
                                تماس بگیرید.
                            </p>
                        </div>
                    )}
                    {!isBlocked && hasPaidDaysLeft && (
                        <div className=" rounded-card border border-warning-1 bg-warning-soft p-3 mb-3">
                            <p className=" text-sm">
                                {persianDays} روز از اشتراک فعلی باقی مانده
                                است. با تغییر به نسخه‌ی کارمزدی این روزها
                                بازگردانده نمی‌شوند.
                            </p>
                        </div>
                    )}
                    {/* Numbers come from the live `CommissionRule` row
                        (type=prt): 2% of the deposit, floored at ۱۰٬۰۰۰ and
                        capped at ۵۰٬۰۰۰ تومان — confirmed against real
                        `Transaction.commission` values. Do NOT source these
                        from `MoneypoolCommission.calculate_portion_commission`;
                        that stepped 900/1200/1500 path does not charge these
                        transactions. */}
                    <div className=" rounded-card border border-brand bg-brand-soft p-4">
                        <div className=" flex items-center gap-2">
                            <img src={PaymentIcon} alt="" aria-hidden />
                            <p className=" text-sm font-bold text-brand-deep">
                                نسخه‌ی کارمزدی
                            </p>
                        </div>
                        <p className=" mt-3 text-xl font-bold text-heading">
                            ۲٪ هر واریز
                        </p>
                        <p className=" mt-1 text-xs text-text-secondary">
                            حداقل ۱۰٬۰۰۰ و حداکثر ۵۰٬۰۰۰ تومان
                        </p>
                    </div>

                    <div className=" my-3 rounded-card bg-white p-3 shadow-card">
                        <p className=" text-sm font-bold text-heading">
                            کارمزد چطور حساب می‌شود؟
                        </p>
                        <div className=" mt-3">
                            {[
                                ['واریز ۵۰۰٬۰۰۰ تومان', '۱۰٬۰۰۰ تومان'],
                                ['واریز ۱٬۰۰۰٬۰۰۰ تومان', '۲۰٬۰۰۰ تومان'],
                                ['واریز ۳٬۰۰۰٬۰۰۰ تومان و بیشتر', '۵۰٬۰۰۰ تومان'],
                            ].map(([label, value]) => (
                                <div
                                    key={label}
                                    className=" flex items-center justify-between gap-2 border-b border-divider py-2 last:border-b-0"
                                >
                                    <span className=" text-xs text-text-secondary">
                                        {label}
                                    </span>
                                    <span className=" text-sm text-text-body">
                                        {value}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className=" mt-3 text-xs leading-6 text-text-secondary">
                            کارمزد ۲٪ مبلغ واریز است و هیچ‌وقت کمتر از ۱۰٬۰۰۰ یا
                            بیشتر از ۵۰٬۰۰۰ تومان نمی‌شود. این مبلغ به واریز عضو
                            اضافه می‌شود و صندوق هزینه‌ی ماهانه‌ای نمی‌دهد. همین
                            کارمزد همه‌ی هزینه‌های درگاه پرداخت، پیامک و
                            نگه‌داری اطلاعات صندوق را پوشش می‌دهد.
                        </p>
                    </div>
                </div>
                <div className="grow-0 mx-2">
                    <div className=" my-1 text-center">
                        <p>نیاز به راهنمایی دارید؟</p>
                        <p
                            className=" mt-1 text-sm font-bold text-brand-deep underline underline-offset-4"
                            onClick={() =>
                                dispatch(
                                    toggleSupportModal({
                                        isOpenSupportModal: true,
                                    })
                                )
                            }
                        >
                            با مشاور همیان تماس بگیرید
                        </p>
                    </div>
                    <button
                        className=" w-full rounded-card bg-brand py-3 text-sm font-bold text-white transition-colors active:bg-brand-strong disabled:opacity-50"
                        disabled={isLoadingMoneypoolManagement || isBlocked}
                        onClick={() => mutate(Number(moneyPool))}
                    >
                        تایید
                    </button>
                </div>
            </div>
            <SupportModal
                show={isOpenSupportModal}
                onHide={() =>
                    dispatch(toggleSupportModal({ isOpenSupportModal: false }))
                }
            />
        </>
    )
}
